package co.uk.sacursoftware.ssinventory.util

import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

class UsageSyncManager(private val repository: SSInventoryRepository) {

    private val _syncStatus = MutableStateFlow<String?>(null)
    val syncStatus: StateFlow<String?> = _syncStatus

    suspend fun syncUsage(selectedReports: List<Report>): Result<Int> {
        try {
            _syncStatus.value = "Fetching all items..."
            val suppliers = repository.getSuppliers().getOrNull() ?: return Result.failure(Exception("Failed to fetch suppliers"))
            
            val allItems = mutableListOf<Item>()
            suppliers.forEach { supplier ->
                repository.getItems(supplier.id).onSuccess { allItems.addAll(it) }
            }

            if (allItems.isEmpty()) return Result.failure(Exception("No items found to sync"))

            // itemId -> List of weekly usage rates from reports
            val itemUsageHistory = mutableMapOf<String, MutableList<Double>>()

            for ((index, report) in selectedReports.withIndex()) {
                _syncStatus.value = "Fetching report details (${index + 1}/${selectedReports.size})..."
                val weeks = DateTimeUtils.calculateWeeksBetween(report.startDate, report.endDate)
                if (weeks <= 0) continue

                repository.getReportDetails(report.id).onSuccess { details ->
                    details.forEach { detail ->
                        val weeklyUsage = detail.usageAmount / weeks
                        itemUsageHistory.getOrPut(detail.itemId) { mutableListOf() }.add(weeklyUsage)
                    }
                }
            }

            var updatedCount = 0
            for ((index, item) in allItems.withIndex()) {
                _syncStatus.value = "Calculating and updating items (${index + 1}/${allItems.size})..."
                
                val history = itemUsageHistory[item.id] ?: continue
                if (history.isEmpty()) continue

                // Calculate average: (Current + Sum of histories) / (1 + Count of histories)
                val totalUsage = item.weeklyUsageRate + history.sum()
                val newAverage = totalUsage / (history.size + 1)

                // Only update if change is significant (> 1%)
                val diff = kotlin.math.abs(newAverage - item.weeklyUsageRate)
                if (diff > (item.weeklyUsageRate * 0.01)) {
                    val request = EditItemRequest(
                        id = item.id,
                        name = item.name,
                        packDescription = item.packDescription,
                        unitMeasureType = item.unitMeasureType,
                        packsPerBox = item.packsPerBox,
                        measurePerPack = item.measurePerPack,
                        weeklyUsageRate = kotlin.math.round(newAverage * 100.0) / 100.0,
                        price = item.price,
                    )
                    repository.editItem(request).onSuccess { updatedCount++ }
                }
            }

            _syncStatus.value = null
            return Result.success(updatedCount)
        } catch (e: Exception) {
            _syncStatus.value = null
            return Result.failure(e)
        }
    }
}
